import { useMutation, useQuery } from "@tanstack/react-query";
import { RotateCcw, Zap } from "lucide-react";

import { useConfirm } from "@/components/feedback/ConfirmDialog";
import { toast } from "@/components/feedback/ToastHost";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { usePermissions } from "@/hooks/usePermissions";
import { errorMessage } from "@/lib/errors";
import { Permission } from "@/lib/permissions";
import {
  getServerStatus,
  restartServices,
  type RestartMode,
} from "@/services/api/server";

/** Restart the backend (and, in development, the frontend) servers. */
export function RestartSection() {
  const { has } = usePermissions();
  const confirm = useConfirm();

  // Poll while a restart is in flight so the draining state stays live.
  const status = useQuery({
    queryKey: ["server-status"],
    queryFn: () => getServerStatus(),
    refetchInterval: (query) => (query.state.data?.restart_requested ? 2000 : false),
  });

  const restart = useMutation({
    mutationFn: (mode: RestartMode) => restartServices(mode),
    onSuccess: (data) => toast.success(data.message),
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!has(Permission.SERVER_RESTART)) return null;

  const busy = restart.isPending || status.data?.restart_requested === true;
  const graceSeconds = status.data?.grace_seconds ?? 60;

  const handleRestart = async (mode: RestartMode) => {
    const graceful = mode === "graceful";
    const ok = await confirm({
      title: graceful ? "Restart services gracefully?" : "Force restart services?",
      description: graceful
        ? "The server will stop accepting new requests, finish in-flight work, and force a restart after the grace period if needed. The page reloads when the servers are back."
        : "Services restart immediately. In-flight requests may be lost. Continue?",
      confirmLabel: graceful ? "Restart gracefully" : "Force restart",
      destructive: !graceful,
    });
    if (ok) restart.mutate(mode);
  };

  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted/40 px-4 py-2">
        <Label className="text-sm font-semibold">Restart services</Label>
        <p className="text-xs text-muted-foreground">
          Restart the backend and frontend servers. Gracefully drains connections and forces a
          restart after {graceSeconds}s; Force restarts immediately.
        </p>
      </div>
      <div className="flex items-center justify-between gap-4 p-4">
        <div className="text-sm text-muted-foreground">
          {status.data?.restart_requested ? (
            status.data.draining ? (
              <span>
                Draining connections — forcing a restart after {graceSeconds}s. The page will reload
                when the servers are back.
              </span>
            ) : (
              <span>Force restart in progress — services are restarting.</span>
            )
          ) : (
            <span>Use when a configuration change requires a fresh process.</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => void handleRestart("graceful")}
          >
            <RotateCcw className="h-4 w-4" />
            Gracefully
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy}
            onClick={() => void handleRestart("force")}
          >
            <Zap className="h-4 w-4" />
            Force
          </Button>
        </div>
      </div>
    </div>
  );
}
